import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { analytics } from '../utils/analytics';

interface NewsletterSignupProps {
  className?: string;
  variant?: 'default' | 'inline' | 'modal';
  title?: string;
  description?: string;
  placeholder?: string;
  buttonText?: string;
}

interface EmailOctopusResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  className = '',
  variant = 'default',
  title = 'Stay Updated with Web Design Insights',
  description = 'Get the latest web design tips, SEO strategies, and business growth insights delivered to your inbox weekly.',
  placeholder = 'Enter your email address',
  buttonText = 'Subscribe'
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // EmailOctopus configuration
  const EMAILOCTOPUS_CONFIG = {
    apiKey: 'eo_e7287cf5ad37d64d72861d2c9e59c7bf25827fd4377068a04ad8a05687367aa9',
    listId: 'Subscribers' // This will need to be the actual list ID from EmailOctopus
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const subscribeToEmailOctopus = async (email: string): Promise<EmailOctopusResponse> => {
    try {
      // First, we need to get the actual list ID from EmailOctopus
      // The list name "Subscribers" needs to be converted to the actual list ID
      
      // For now, we'll use a direct API call to EmailOctopus
      // Note: In production, this should be done through your backend to keep the API key secure
      
      const response = await fetch(`https://emailoctopus.com/api/1.6/lists`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      let listId = EMAILOCTOPUS_CONFIG.listId;
      
      if (response.ok) {
        const listsData = await response.json();
        // Find the list by name
        const targetList = listsData.data?.find((list: any) => 
          list.name === 'Subscribers'
        );
        if (targetList) {
          listId = targetList.id;
        }
      }

      // Now subscribe to the list
      const subscribeResponse = await fetch(`https://emailoctopus.com/api/1.6/lists/${listId}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          api_key: EMAILOCTOPUS_CONFIG.apiKey,
          email_address: email,
          fields: {
            FirstName: '',
            LastName: ''
          },
          tags: ['website-signup'],
          status: 'SUBSCRIBED'
        })
      });

      const subscribeData = await subscribeResponse.json();

      if (subscribeResponse.ok) {
        return {
          success: true,
          message: 'Successfully subscribed to our newsletter!',
          data: subscribeData
        };
      } else {
        // Handle specific EmailOctopus errors
        let errorMessage = 'Failed to subscribe. Please try again.';
        
        if (subscribeData.error) {
          switch (subscribeData.error.code) {
            case 'MEMBER_EXISTS_WITH_EMAIL_ADDRESS':
              errorMessage = 'You are already subscribed to our newsletter!';
              break;
            case 'INVALID_PARAMETERS':
              errorMessage = 'Please enter a valid email address.';
              break;
            case 'FORBIDDEN':
              errorMessage = 'Subscription failed. Please try again later.';
              break;
            default:
              errorMessage = subscribeData.error.message || errorMessage;
          }
        }

        return {
          success: false,
          message: errorMessage,
          data: subscribeData
        };
      }
    } catch (error) {
      console.error('EmailOctopus subscription error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const result = await subscribeToEmailOctopus(email);

      if (result.success) {
        setStatus('success');
        setMessage(result.message);
        setEmail(''); // Clear the form
        
        // Track conversion event with Google Analytics
        analytics.trackNewsletterSignup('emailoctopus');

        // Store subscription status
        localStorage.setItem('newsletter-subscribed', 'true');
      } else {
        setStatus('error');
        setMessage(result.message);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setMessage('An unexpected error occurred. Please try again later.');
    }
  };

  const resetForm = () => {
    setStatus('idle');
    setMessage('');
    setEmail('');
  };

  // Variant-specific styling
  const getVariantClasses = () => {
    switch (variant) {
      case 'inline':
        return 'flex flex-row items-center space-x-2';
      case 'modal':
        return 'max-w-md mx-auto';
      default:
        return 'max-w-md mx-auto';
    }
  };

  if (status === 'success') {
    return (
      <div className={`${className} ${getVariantClasses()}`}>
        <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-green-800 mb-2">Thank You!</h3>
          <p className="text-green-700 mb-4">{message}</p>
          <p className="text-sm text-green-600 mb-4">
            Please check your email and confirm your subscription to start receiving our newsletter.
          </p>
          <button
            onClick={resetForm}
            className="text-green-600 hover:text-green-700 font-medium text-sm"
            aria-label="Subscribe another email"
          >
            Subscribe another email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} ${getVariantClasses()}`}>
      {variant !== 'inline' && (
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600">{description}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={variant === 'inline' ? 'flex flex-1' : 'space-y-4'}>
        <div className={variant === 'inline' ? 'flex-1' : ''}>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              disabled={status === 'loading'}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent ${
                status === 'error' ? 'border-red-300' : 'border-gray-300'
              } ${status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label="Email address"
              aria-invalid={status === 'error'}
              aria-describedby={status === 'error' ? "newsletter-error" : undefined}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className={`${
            variant === 'inline' ? 'ml-2' : 'w-full'
          } bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
          aria-label={status === 'loading' ? 'Subscribing...' : buttonText}
        >
          {status === 'loading' ? (
            <>
              <Loader className="animate-spin h-5 w-5 mr-2" aria-hidden="true" />
              <span>Subscribing...</span>
            </>
          ) : (
            buttonText
          )}
        </button>
      </form>

      {message && status === 'error' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center" id="newsletter-error" role="alert">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" aria-hidden="true" />
          <p className="text-red-700 text-sm">{message}</p>
        </div>
      )}

      {variant !== 'inline' && (
        <p className="text-xs text-gray-500 mt-4 text-center">
          No spam, unsubscribe at any time. We respect your privacy.
        </p>
      )}
    </div>
  );
};